import { fetchSendingRequests, fetchSendings } from '@api';
import { DEFAULT_PER_PAGE } from '@constants';
import {
  DOCUMENTS_CACHE_QUERY_KEY,
  DOCUMENTS_CACHE_RETRY_COUNT,
  DOCUMENTS_CACHE_STALE_TIME,
  DOCUMENTS_PATH,
} from '@constants/documents.constants';
import {
  AnySendingSearchParams,
  DashboardSearchParams,
  DocumentsList,
  EntitiesBase,
  FeatureFlags,
  Professional,
  ProfessionalDto,
  SendingRequestBundle,
  SendingRequestDto,
  SendingsDashboardType,
} from '@domain/types';
import { Dispatch } from '@reduxjs/toolkit';
import { Http } from '@services/http';
import { documentDtoToSendingRequest } from '@store/adapters';
import { updateEntitiesAction } from '@store/documents/documents.actions';
import {
  DocumentDashboardType,
  INITIAL_SEARCH_PARAMS,
  SendingRequestDtoFromType,
} from '@store/documents/documents.state';
import { Query, QueryClient, QueryKey } from '@tanstack/react-query';
import { allDocumentCachesKey, documentDetailKey } from '@utils/documentCacheKeys.util';
import { hasActiveFilters } from './dashboardFilters.util';
import { normalizeEntitiesFromAPI } from './entitiesAdapter.util';
import { professionalToIdentityReference } from './identities.util';
import { parseSearchParams } from './search.util';

// Re-export for convenience
export { normalizeEntitiesFromAPI };
export { hasActiveFilters } from './dashboardFilters.util';

/**
 * Helper to merge arrays of entities by ID
 * Newer entities override older ones with the same ID
 */
function mergeEntityArrays<T extends { id: string }>(
  existing: T[] = [],
  newEntities: T[] = [],
): T[] {
  const existingMap = new Map(existing.map((e) => [e.id, e]));
  newEntities.forEach((e) => existingMap.set(e.id, e));
  return Array.from(existingMap.values());
}

/**
 * Every documents key is `[DOCUMENTS_CACHE_QUERY_KEY, dashboardType, identity | 'all', searchParams?]`
 * — always 3 base segments, with the prepared searchParams (when there is one) landing right after
 * them (see `documentsDashboardCache.characterization.spec.ts`, which pins both facts).
 *
 * This module is the only place that knows the layout: build a key with
 * `getDashboardDocumentsQueryKey` or `dashboardDocumentsKeyPrefix`, read one with `searchParamsOf`,
 * never by indexing it yourself. Same rule as `sendingRequestsV2Cache.util.ts`, whose readers pass
 * predicates built from its helpers rather than reaching into a key.
 */
const SEARCH_PARAMS_INDEX = 3;

/**
 * The 2-segment prefix that matches every identity bucket of a dashboard at once — deliberately
 * shorter than `getDashboardDocumentsQueryKey`'s own output, which always fixes a third segment to
 * an identity or `'all'`. For callers that want every bucket, not one.
 */
const dashboardDocumentsKeyPrefix = (dashboardType: DocumentDashboardType) =>
  [DOCUMENTS_CACHE_QUERY_KEY, dashboardType] as const;

/**
 * The prepared searchParams a documents key carries, or undefined for a key that carries none —
 * a base key, or one whose shape has moved.
 *
 * The shape guard buys a truthful return type, not a behaviour: both callers below happen to
 * survive a non-object segment anyway (`hasActiveFilters` reads named fields off it and finds
 * none; the page predicate reads `.page` and finds none), so deleting the guard changes nothing
 * observable today — verified by mutation. It earns its keep because this is now a named reader
 * other code will use, and a caller doing `searchParamsOf(key)?.documentType` deserves a type
 * that is not lying to it.
 */
const searchParamsOf = (queryKey: QueryKey): DashboardSearchParams | undefined => {
  const segment = (queryKey as unknown[])[SEARCH_PARAMS_INDEX];

  return segment != null && typeof segment === 'object' && !Array.isArray(segment)
    ? (segment as DashboardSearchParams)
    : undefined;
};

export const getDashboardDocumentsQueryKey = ({
  dashboardType,
  identity,
  search,
}: {
  dashboardType: DocumentDashboardType;
  identity?: string;
  search?: { searchParams: AnySendingSearchParams; featureFlags: FeatureFlags };
}) => {
  const key = [
    ...dashboardDocumentsKeyPrefix(dashboardType),
    ...(identity && dashboardType === SendingsDashboardType.Draft ? [identity] : ['all']),
  ];

  if (search) {
    const searchParamsForQueryKey = prepareSearchParamsForCaching({
      searchParams: search.searchParams,
      currentIdentity: identity,
      dashboardType,
      featureFlags: search.featureFlags,
    });
    return [...key, searchParamsForQueryKey];
  }
  return key;
};

/**
 * Query fn that fetch sending requests and update its entities in redux store
 */
export const sendingRequestsQueryFn = async ({
  searchParams,
  dispatchFn,
  currentIdentity,
  dashboardType,
  featureFlags,
}: {
  searchParams: DashboardSearchParams;
  dispatchFn: Dispatch<any>;
  currentIdentity?: string;
  dashboardType: SendingsDashboardType;
  featureFlags?: FeatureFlags;
}) => {
  const preparedSearchParams = prepareSearchParamsForCaching({
    searchParams,
    currentIdentity,
    dashboardType,
    featureFlags,
  });
  const data = await fetchSendingRequests(preparedSearchParams);
  const { documents, ...entities } = data.entities;
  dispatchFn(updateEntitiesAction(entities));
  return data;
};

/**
 * Query fn that fetch sendings and update its entities in redux store
 */
export const sendingsQueryFn = async ({
  searchParams,
  dispatchFn,
  dashboardType,
}: {
  searchParams: DashboardSearchParams;
  dispatchFn: Dispatch<any>;
  dashboardType: SendingsDashboardType.Inbox | SendingsDashboardType.Archive;
}) => {
  const preparedSearchParams = prepareSearchParamsForCaching({
    searchParams,
    dashboardType,
  });
  const data = await fetchSendings(preparedSearchParams);
  const { documents, ...entities } = data.entities;
  dispatchFn(updateEntitiesAction(entities));
  return data;
};

/**
 * Function applied after selecting a documents list from a useQuery hook
 * Normalizes entities from the API response instead of reading from Redux
 */
export const transformDocumentsList = (response: DocumentsList) => {
  // Normalize entities from the API response
  const entities = normalizeEntitiesFromAPI(response);

  return {
    documents: response?.entities
      ? (response?.entities.documents ?? [])
          .filter(
            (document, index, docs) =>
              docs.findIndex((docToFilter) => docToFilter.id === document.id) === index,
          )
          .map((document) => documentDtoToSendingRequest(document, entities))
      : [],
    total: response.total,
  };
};

/**
 * React Query handler to fetch documents
 */
export const fetchDocumentsHandler = (
  searchParams: DashboardSearchParams,
): Promise<DocumentsList> =>
  Http.getInstance().get({ path: DOCUMENTS_PATH, searchParams: parseSearchParams(searchParams) });

/**
 * Prepare searchParams before using them as query key or in fetching handler
 */
export const prepareSearchParamsForCaching = <T extends AnySendingSearchParams>({
  searchParams,
  currentIdentity,
  dashboardType,
  featureFlags,
}: {
  searchParams: T;
  currentIdentity?: string;
  dashboardType: DocumentDashboardType;
  featureFlags?: FeatureFlags;
}): T => {
  let preparedSearchParams: T;
  switch (dashboardType) {
    case SendingsDashboardType.Draft:
      return prepareDraftSearchParamsForCaching({
        searchParams,
        currentIdentity,
        featureFlags,
      });
    case SendingsDashboardType.Sent:
      preparedSearchParams = prepareSentSearchParamsForCaching({ searchParams });
      break;
    case SendingsDashboardType.Archive:
      preparedSearchParams = {
        ...searchParams,
        archived: true,
      };
      break;
    case SendingsDashboardType.Inbox:
      preparedSearchParams = {
        ...searchParams,
        archived: false,
      };
      break;
    default:
      preparedSearchParams = searchParams;
      break;
  }

  return preparedSearchParams;
};

const prepareDraftSearchParamsForCaching = <T extends AnySendingSearchParams>({
  searchParams,
  currentIdentity,
  featureFlags,
}: {
  searchParams: T;
  currentIdentity?: string;
  featureFlags?: FeatureFlags;
}): T => {
  const useNewUploadUI = featureFlags?.['enable-new-import-analyze-flow'];
  const statuses = useNewUploadUI
    ? searchParams?.statuses?.filter((status) => status !== 'draft')
    : searchParams?.statuses;

  return {
    ...searchParams,
    ...(statuses && { statuses }),
    ...(currentIdentity && { selectedSenderFilter: undefined }),
    ...(currentIdentity && {
      strictSender: currentIdentity,
    }),
  };
};

export const prepareSentSearchParamsForCaching = <T extends AnySendingSearchParams>({
  searchParams,
}: {
  searchParams: T;
}): T => ({
  ...searchParams,
});

// Functions using queryClient:
export const getDashboardDataInCache = ({
  dashboardType,
  identity,
  queryClient,
  search,
}: {
  dashboardType: SendingsDashboardType;
  identity?: string;
  queryClient: QueryClient;
  search?: { searchParams: DashboardSearchParams; featureFlags: FeatureFlags };
}) =>
  queryClient.getQueryData(
    getDashboardDocumentsQueryKey({ dashboardType, identity, search }),
  ) as DocumentsList;

/**
 * Add given document in the specified draft cache
 * If newDocumentEntities is provided, merge them with existing entities in cache
 */
export const addDraftDocumentInCache = ({
  newDocument,
  newDocumentEntities,
  identity,
  searchParams = INITIAL_SEARCH_PARAMS,
  refetchType = 'inactive',
  queryClient,
  featureFlags,
}: {
  newDocument: SendingRequestDto;
  newDocumentEntities?: EntitiesBase;
  identity?: string;
  searchParams?: DashboardSearchParams;
  refetchType?: 'all' | 'active' | 'inactive' | 'none';
  queryClient: QueryClient;
  featureFlags: FeatureFlags;
}) => {
  const documentsInCache = getDashboardDataInCache({
    dashboardType: SendingsDashboardType.Draft,
    identity,
    search: { searchParams, featureFlags },
    queryClient,
  });

  // Invalidate current queries
  queryClient.cancelQueries({
    queryKey: getDashboardDocumentsQueryKey({
      dashboardType: SendingsDashboardType.Draft,
      identity,
      search: { searchParams, featureFlags },
    }),
  });

  // If cache for current dashboard is empty, force query refetch
  if (documentsInCache === undefined) {
    queryClient.refetchQueries({
      queryKey: getDashboardDocumentsQueryKey({
        dashboardType: SendingsDashboardType.Draft,
        identity,
        search: { searchParams, featureFlags },
      }),
    });
    return;
  }

  // If any user filter is active, the new document may not satisfy it — invalidate instead
  if (hasActiveFilters(searchParams)) {
    queryClient.invalidateQueries({
      queryKey: getDashboardDocumentsQueryKey({
        dashboardType: SendingsDashboardType.Draft,
        identity,
        search: { searchParams, featureFlags },
      }),
      refetchType,
    });
    return;
  }

  // Add new document to current cache. Ensure to keep DEFAULT_PER_PAGE docs in the current page
  queryClient.setQueryData(
    getDashboardDocumentsQueryKey({
      dashboardType: SendingsDashboardType.Draft,
      identity,
      search: { searchParams, featureFlags },
    }),
    (currentDocuments: DocumentsList | undefined) =>
      currentDocuments?.entities?.documents
        ? {
            ...currentDocuments,
            entities: {
              ...currentDocuments.entities,
              documents: [
                newDocument,
                ...(currentDocuments.entities.documents.length >= DEFAULT_PER_PAGE
                  ? currentDocuments.entities.documents.slice(0, -1)
                  : currentDocuments.entities.documents),
              ],
              // Merge new document entities with existing ones
              patients: newDocumentEntities
                ? mergeEntityArrays(
                    currentDocuments.entities.patients,
                    newDocumentEntities.patients,
                  )
                : currentDocuments.entities.patients,
              practitioners: newDocumentEntities
                ? mergeEntityArrays(
                    currentDocuments.entities.practitioners,
                    newDocumentEntities.practitioners,
                  )
                : currentDocuments.entities.practitioners,
              organizations: newDocumentEntities
                ? mergeEntityArrays(
                    currentDocuments.entities.organizations,
                    newDocumentEntities.organizations,
                  )
                : currentDocuments.entities.organizations,
              practitionerRoles: newDocumentEntities
                ? mergeEntityArrays(
                    currentDocuments.entities.practitionerRoles,
                    newDocumentEntities.practitionerRoles,
                  )
                : currentDocuments.entities.practitionerRoles,
            },
          }
        : currentDocuments,
  );

  // Invalidate all queries of next pages that already exists in cache
  queryClient.invalidateQueries(
    {
      queryKey: getDashboardDocumentsQueryKey({
        dashboardType: SendingsDashboardType.Draft,
        identity,
      }),
      refetchType,
      predicate: (query) => (searchParamsOf(query.queryKey)?.page ?? 0) > 1,
      exact: false,
    },
    { cancelRefetch: false },
  );
};

/**
 * Update given document in the specified cache
 * If updatedDocumentEntities is provided, merge them with existing entities in cache
 */
export const updateDocumentInDashboardCache = ({
  updatedDocument,
  updatedDocumentEntities,
  dashboardType = SendingsDashboardType.Draft,
  queryClient,
}: {
  updatedDocument: SendingRequestDto;
  updatedDocumentEntities?: EntitiesBase;
  dashboardType?: SendingsDashboardType;
  queryClient: QueryClient;
}) => {
  const identity =
    dashboardType === SendingsDashboardType.Draft
      ? professionalToIdentityReference(updatedDocument.sender)
      : undefined;
  const updater = (currentDocuments: DocumentsList | undefined) =>
    currentDocuments?.entities?.documents
      ? {
          ...currentDocuments,
          entities: {
            ...currentDocuments.entities,
            documents: [
              ...currentDocuments.entities.documents.map((doc) =>
                doc.id === updatedDocument.id ? updatedDocument : doc,
              ),
            ],
            // Merge updated document entities with existing ones
            patients: updatedDocumentEntities
              ? mergeEntityArrays(
                  currentDocuments.entities.patients,
                  updatedDocumentEntities.patients,
                )
              : currentDocuments.entities.patients,
            practitioners: updatedDocumentEntities
              ? mergeEntityArrays(
                  currentDocuments.entities.practitioners,
                  updatedDocumentEntities.practitioners,
                )
              : currentDocuments.entities.practitioners,
            organizations: updatedDocumentEntities
              ? mergeEntityArrays(
                  currentDocuments.entities.organizations,
                  updatedDocumentEntities.organizations,
                )
              : currentDocuments.entities.organizations,
            practitionerRoles: updatedDocumentEntities
              ? mergeEntityArrays(
                  currentDocuments.entities.practitionerRoles,
                  updatedDocumentEntities.practitionerRoles,
                )
              : currentDocuments.entities.practitionerRoles,
          },
        }
      : currentDocuments;

  const updateOrInvalidate = (baseQueryKey: QueryKey) => {
    const matchingQueries = queryClient.getQueriesData<DocumentsList>({
      queryKey: baseQueryKey,
      exact: false,
    });

    for (const [queryKey] of matchingQueries) {
      const searchParamsInKey = searchParamsOf(queryKey);

      if (searchParamsInKey && hasActiveFilters(searchParamsInKey)) {
        // Cache avec filtres actifs : invalider pour que le serveur renvoie la liste correcte.
        // Mettre à jour en place injecterait des documents ne correspondant plus au filtre.
        queryClient.invalidateQueries({ queryKey, exact: true });
      } else {
        queryClient.setQueryData(queryKey, updater);
      }
    }
  };

  updateOrInvalidate(getDashboardDocumentsQueryKey({ dashboardType, identity }));

  if (dashboardType === SendingsDashboardType.Draft) {
    updateOrInvalidate(
      getDashboardDocumentsQueryKey({ dashboardType: SendingsDashboardType.Draft }),
    );
  }
};

export const updateDocumentInDetailCache = ({
  queryClient,
  bundle,
}: {
  queryClient: QueryClient;
  bundle: SendingRequestBundle;
}) => {
  queryClient.setQueryData(documentDetailKey(bundle.document.id), bundle);
};

/**
 * Invalidate cache that match given params
 * Returns a Promise that resolves when refetch is complete (if refetchType is not 'none')
 */
export const invalidateDashboardCache = ({
  dashboardType,
  identity,
  type = undefined,
  refetchType = 'inactive',
  search,
  predicate,
  exact,
  queryClient,
}: {
  dashboardType: SendingsDashboardType | 'errors';
  identity?: string;
  search?: { searchParams: DashboardSearchParams; featureFlags: FeatureFlags };
  type?: 'all' | 'active' | 'inactive';
  refetchType?: 'all' | 'active' | 'inactive' | 'none';
  predicate?: (query: Query<unknown, unknown, unknown>) => boolean;
  exact?: boolean;
  queryClient: QueryClient;
}) => {
  return queryClient.invalidateQueries(
    {
      queryKey: getDashboardDocumentsQueryKey({ dashboardType, identity, search }),
      type,
      refetchType,
      predicate,
      exact,
    },
    { cancelRefetch: false },
  );
};

export const getDocumentsInCache = <T extends DocumentDashboardType>({
  queryClient,
  dashboardType,
  identity,
  search,
}: {
  dashboardType: T;
  search?: { searchParams: DashboardSearchParams; featureFlags: FeatureFlags };
  identity?: string;
  queryClient: QueryClient;
}) =>
  queryClient.getQueryData<DocumentsList>(
    getDashboardDocumentsQueryKey({
      dashboardType,
      identity,
      search,
    }),
  )?.entities?.documents;

/**
 * Find first occurence of document with given ID in cache
 */
export const findDocumentInCache = <T extends DocumentDashboardType>({
  dashboardType,
  identity,
  search,
  documentId,
  queryClient,
}: {
  dashboardType: T;
  search?: { searchParams: DashboardSearchParams; featureFlags: FeatureFlags };
  identity?: string;
  documentId: string;
  queryClient: QueryClient;
}) => {
  const currentDashboardDocumentsInCache = getDocumentsInCache({
    queryClient,
    dashboardType,
    identity,
    search,
  });
  return (currentDashboardDocumentsInCache as SendingRequestDtoFromType<T>[])?.find(
    (doc) => doc.id === documentId,
  );
};

/**
 * Find all occurences of document with given ID in cache
 */
export const findDocumentsInCache = ({
  dashboardType,
  identity,
  search,
  documentId,
  queryClient,
}: {
  dashboardType: SendingsDashboardType;
  search?: { searchParams: DashboardSearchParams; featureFlags: FeatureFlags };
  identity?: string;
  documentId: string;
  queryClient: QueryClient;
}) => {
  const queryKey = getDashboardDocumentsQueryKey({ dashboardType, identity, search });
  const currentQueriesCache = queryClient.getQueriesData<DocumentsList>({ queryKey });
  return currentQueriesCache.reduce(
    (documentsInCache: SendingRequestDto[], query: [QueryKey, DocumentsList | undefined]) => {
      const docInCache = query[1]?.entities?.documents?.find((doc) => doc.id === documentId);
      return docInCache ? [...documentsInCache, docInCache] : documentsInCache;
    },
    [],
  );
};

/**
 * The v1 mirror of `findSenderIdentityV2InCache`: whose Draft buckets a write belongs to, read off
 * the document's last cached dashboard row.
 *
 * Scans every identity bucket and every search variant, because the caller holds a document id and
 * nothing else — unlike `findDocumentsInCache`, which needs the identity to build its key. Returns
 * undefined when genuinely unknown; the identity the user has selected is not a fallback, see the
 * Attribution section of `lifen-sending/CONTEXT.md`.
 */
export const findSenderIdentityInCache = (
  queryClient: QueryClient,
  documentId: string,
): string | undefined => {
  const entries = queryClient.getQueriesData<DocumentsList>({
    queryKey: dashboardDocumentsKeyPrefix(SendingsDashboardType.Draft),
  });

  for (const [, data] of entries) {
    const document = data?.entities?.documents?.find((candidate) => candidate.id === documentId);
    if (document?.sender) return professionalToIdentityReference(document.sender);
  }

  return undefined;
};

/**
 * Invalidate all draft caches with given identity
 */
export const refreshDraftDocumentsInCache = ({
  identity,
  queryClient,
}: {
  identity: string | undefined;
  queryClient: QueryClient;
}) => {
  queryClient.invalidateQueries({
    queryKey: getDashboardDocumentsQueryKey({
      dashboardType: SendingsDashboardType.Draft,
      identity,
    }),
    refetchType: 'active',
  });
};

/**
 * Invalidate cache for Document details, but doesn't trigger a refetch
 */
export const invalidateDraftDocumentsInCache = ({
  documentId,
  queryClient,
}: {
  documentId: string;
  queryClient: QueryClient;
}) => {
  queryClient.invalidateQueries({
    queryKey: documentDetailKey(documentId),
    refetchType: 'none',
  });
};

/**
 * Find document ID in cache and invalidate corresponding caches
 */
export const refreshDraftCacheAfterActionOnOneDocument = ({
  documentId,
  currentIdentity,
  search,
  queryClient,
}: {
  documentId: string;
  currentIdentity?: string;
  search?: { searchParams: DashboardSearchParams; featureFlags: FeatureFlags };
  queryClient: QueryClient;
}) => {
  const documentsInCache = findDocumentsInCache({
    dashboardType: SendingsDashboardType.Draft,
    documentId,
    identity: currentIdentity,
    search,
    queryClient,
  });

  const identity =
    documentsInCache.length > 0
      ? professionalToIdentityReference(documentsInCache[0].sender)
      : currentIdentity;

  if (identity) {
    refreshDraftDocumentsInCache({ identity, queryClient });
  }
  refreshDraftDocumentsInCache({ identity: 'all', queryClient });

  return { documentsInCache, identity };
};

/**
 * Find document IDs in cache and invalidate corresponding caches
 */
export const refreshDraftCacheAfterActionOnManyDocuments = ({
  documentIds,
  currentIdentity,
  search,
  queryClient,
}: {
  documentIds: string[];
  currentIdentity?: string;
  search?: { searchParams: DashboardSearchParams; featureFlags: FeatureFlags };
  queryClient: QueryClient;
}) => {
  const identities: string[] = [];
  let allDocumentsInCache: SendingRequestDto[] = [];

  for (const documentId of documentIds) {
    const documentsInCache = findDocumentsInCache({
      dashboardType: SendingsDashboardType.Draft,
      documentId: `${documentId}`,
      identity: currentIdentity ?? 'all',
      search,
      queryClient,
    });

    const documentIdentity =
      documentsInCache?.length > 0
        ? professionalToIdentityReference(documentsInCache[0].sender)
        : currentIdentity;

    if (documentIdentity && !identities.find((identity) => identity === documentIdentity)) {
      identities.push(documentIdentity);
    }

    allDocumentsInCache = [...allDocumentsInCache, ...documentsInCache];
  }

  for (const identity of identities) {
    refreshDraftDocumentsInCache({ identity, queryClient });
  }

  refreshDraftDocumentsInCache({ identity: 'all', queryClient });

  return { documentsInCache: allDocumentsInCache, identities };
};

/**
 * Invalidate Draft dashboards for a document mutation
 * Invalidates both the general dashboard and the sender's identity-specific dashboard
 */
export const invalidateDraftDashboardForDocument = ({
  queryClient,
  sender,
}: {
  queryClient: QueryClient;
  sender?: Professional | ProfessionalDto | { type: string; id: string };
}) => {
  // Invalidate general Draft dashboard
  invalidateDashboardCache({
    dashboardType: SendingsDashboardType.Draft,
    identity: undefined,
    refetchType: 'none',
    queryClient,
  });

  // Invalidate sender's identity dashboard if sender exists
  if (sender) {
    const identityRef = professionalToIdentityReference(sender);
    invalidateDashboardCache({
      dashboardType: SendingsDashboardType.Draft,
      identity: identityRef,
      refetchType: 'none',
      queryClient,
    });
  }
};

/**
 * Prefetch first page of sending request dashboard type
 */
export const prefetchSendingRequests = async ({
  dashboardType,
  searchParams,
  dispatchFn,
  currentIdentity,
  queryClient,
  featureFlags,
}: {
  dashboardType: SendingsDashboardType;
  searchParams: DashboardSearchParams;
  dispatchFn: Dispatch<any>;
  currentIdentity?: string;
  queryClient: QueryClient;
  featureFlags: FeatureFlags;
}) => {
  const queryFn = () =>
    dashboardType === SendingsDashboardType.Inbox || dashboardType === SendingsDashboardType.Archive
      ? sendingsQueryFn({
          searchParams,
          dashboardType,
          dispatchFn,
        })
      : sendingRequestsQueryFn({
          searchParams,
          currentIdentity,
          dashboardType,
          dispatchFn,
          featureFlags,
        });
  await queryClient.prefetchQuery({
    queryKey: getDashboardDocumentsQueryKey({
      dashboardType,
      identity: currentIdentity,
      search: { searchParams, featureFlags },
    }),
    queryFn,
    staleTime: DOCUMENTS_CACHE_STALE_TIME,
    retry: DOCUMENTS_CACHE_RETRY_COUNT,
  });
};

/**
 * Delay refresh of sent dashboard cache (doc must have been sent before)
 */
export const delaySentDashboardCacheRefresh = ({ queryClient }: { queryClient: QueryClient }) =>
  setTimeout(() => {
    invalidateDashboardCache({
      dashboardType: SendingsDashboardType.Sent,
      refetchType: 'active',
      exact: false,
      queryClient,
    });
  }, 3000);

export async function invalidateAllSendingRequestCache(queryClient: QueryClient) {
  Promise.all([queryClient.invalidateQueries({ queryKey: allDocumentCachesKey() })]);
}
